import type { BootstrapPayload } from "../../shared/contracts/bootstrap.js";
import type { RoundSnapshot } from "../../domain/round/round-types.js";
import type { DeviceAccountCache } from "../data/device-account-cache.js";
import type { RoundJournal } from "../data/round-journal.js";

interface AccountLoader {
  loadBootstrap(): Promise<BootstrapPayload>;
  cache: Pick<DeviceAccountCache, "get" | "save">;
  journal: Pick<
    RoundJournal,
    "getLatestRecoverableSnapshot" | "saveSnapshot" | "hasUnsynced"
  >;
  isCurrent(): boolean;
}

export interface LoadedWorkspaceAccount {
  data: BootstrapPayload;
  round: RoundSnapshot | null;
  hasQueued: boolean;
  hasConflicts: boolean;
  usedCache: boolean;
}

/** Validate ownership before writing either the bootstrap cache or round partition. */
export async function loadWorkspaceAccount(
  accountId: string,
  deps: AccountLoader,
): Promise<LoadedWorkspaceAccount> {
  let data: BootstrapPayload;
  let usedCache = false;
  try {
    data = await deps.loadBootstrap();
  } catch (error) {
    requireCurrent(deps);
    const cached = await deps.cache.get(accountId);
    if (!cached) throw error;
    data = cached.bootstrap;
    usedCache = true;
  }
  requireCurrent(deps);
  if (data.account.id !== accountId)
    throw new Error("The signed-in account changed. Please reload.");
  const localRound = await deps.journal.getLatestRecoverableSnapshot(accountId);
  requireCurrent(deps);
  if (!usedCache) await deps.cache.save(data);
  requireCurrent(deps);
  const round = localRound ?? data.activeRound;
  if (!localRound && round) await deps.journal.saveSnapshot(accountId, round);
  const hasQueued = await deps.journal.hasUnsynced(accountId);
  requireCurrent(deps);
  return {
    data,
    round,
    hasQueued,
    hasConflicts: data.openConflicts.length > 0,
    usedCache,
  };
}

function requireCurrent(deps: AccountLoader): void {
  if (!deps.isCurrent())
    throw new Error("The signed-in account changed. Please reload.");
}
